import { useEffect, useRef, useState } from "react";
import { api, thumbUrl } from "../lib/api";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import RetryImage from "../components/RetryImage";
import UploadModal from "../components/UploadModal";
import { useConfirm } from "../components/ConfirmDialog";
import {
    Upload,
    Trash2,
    FileJson,
    Sparkles,
    Loader2,
    GalleryHorizontalEnd,
    Star,
    Flag,
    ChevronLeft,
    ChevronRight,
    Folder,
    X,
    CheckCircle2,
    GripVertical,
    ArrowRightLeft,
    AtSign,
    Check,
    Pencil,
} from "lucide-react";
import { toast } from "sonner";

const PIECE_TYPES = [
    { id: "social_post", label: "Redes sociales", short: "Redes", color: "#ff9ec7" },
    { id: "website_banner", label: "Banner web", short: "Banner", color: "#8fe7d0" },
    { id: "ppt_slide", label: "Slide PPT", short: "PPT", color: "#ffbe7b" },
    { id: "other", label: "Otro", short: "Otro", color: "#a5a6ff" },
];

export default function References() {
    const [tab, setTab] = useState("own");
    const [view, setView] = useState("pieces"); // pieces | carousels
    const [items, setItems] = useState([]);
    const [carousels, setCarousels] = useState([]);
    const [loading, setLoading] = useState(true);
    // Pagination state for the pieces grid. We cap each network call at
    // PAGE_SIZE images and append more via the "Cargar más" button or
    // automatic infinite scroll. This keeps the initial render snappy
    // even on brands with thousands of references.
    const PAGE_SIZE = 48;
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [openAnalysisId, setOpenAnalysisId] = useState(null);
    const [analyzing, setAnalyzing] = useState({});
    const [typeFilter, setTypeFilter] = useState("all");
    const [openCarousel, setOpenCarousel] = useState(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const confirm = useConfirm();

    const load = () => {
        if (tab === "brand") return; // logos have their own pane
        setLoading(true);
        Promise.all([
            api.get(`/references?category=${tab}&skip=0&limit=${PAGE_SIZE}`),
            api.get(`/references/carousels?category=${tab}`),
        ])
            .then(([r1, r2]) => {
                const data = r1.data || {};
                // Backend returns { items, total, has_more } now; we
                // still accept a bare array for backwards compat in case
                // an older route or proxy strips the envelope.
                const list = Array.isArray(data) ? data : data.items || [];
                setItems(list);
                setTotal(Array.isArray(data) ? list.length : data.total || list.length);
                setHasMore(Array.isArray(data) ? false : !!data.has_more);
                setCarousels(r2.data || []);
            })
            .finally(() => setLoading(false));
    };

    const loadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const r = await api.get(
                `/references?category=${tab}&skip=${items.length}&limit=${PAGE_SIZE}`,
            );
            const data = r.data || {};
            const more = Array.isArray(data) ? data : data.items || [];
            setItems((prev) => [...prev, ...more]);
            setHasMore(Array.isArray(data) ? false : !!data.has_more);
        } catch {
            toast.error("No se pudo cargar más");
        } finally {
            setLoadingMore(false);
        }
    };
    useEffect(load, [tab]);

    useEffect(() => {
        const onBrand = () => load();
        window.addEventListener("kc:brand-changed", onBrand);
        return () => window.removeEventListener("kc:brand-changed", onBrand);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // Poll for unanalyzed items every 6s — vision analysis runs in background
    useEffect(() => {
        if (tab === "brand") return;
        const hasPending =
            items.some((i) => !i.analysis) ||
            carousels.some((c) => c.slides?.some((s) => !s.analysis));
        if (!hasPending) return;
        const t = setInterval(() => {
            // Re-fetch only the currently visible window so the poll
            // doesn't undo a "Cargar más" by re-replacing items with the
            // first page. We request up to items.length (rounded up to
            // PAGE_SIZE multiples) — small enough to stay fast.
            const window = Math.max(items.length, PAGE_SIZE);
            Promise.all([
                api.get(`/references?category=${tab}&skip=0&limit=${window}`),
                api.get(`/references/carousels?category=${tab}`),
            ])
                .then(([r1, r2]) => {
                    const data = r1.data || {};
                    const list = Array.isArray(data) ? data : data.items || [];
                    setItems(list);
                    setTotal(Array.isArray(data) ? list.length : data.total || list.length);
                    setHasMore(Array.isArray(data) ? false : !!data.has_more);
                    setCarousels(r2.data || []);
                })
                .catch(() => {});
        }, 6000);
        return () => clearInterval(t);
    }, [items, carousels, tab]);

    const reanalyze = async (id) => {
        setAnalyzing((s) => ({ ...s, [id]: true }));
        try {
            await api.post(`/references/${id}/analyze`);
            toast.success("Análisis actualizado");
            load();
        } catch {
            toast.error("Error analizando");
        } finally {
            setAnalyzing((s) => ({ ...s, [id]: false }));
        }
    };

    // Patch the local items array in place after a slug change so the
    // card re-renders without needing a full refetch.
    const applyLocalSlug = (id, newSlug) => {
        setItems((prev) =>
            prev.map((it) => (it.id === id ? { ...it, slug: newSlug } : it)),
        );
    };

    const del = async (id) => {
        const ok = await confirm({
            title: "¿Eliminar esta referencia?",
            description: "Se quitará del portafolio de tu marca y ya no podrá usarse como punto base en nuevos chats.",
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        await api.delete(`/references/${id}`);
        load();
    };

    const delCarousel = async (gid) => {
        const ok = await confirm({
            title: "¿Eliminar el carrusel completo?",
            description: "Se eliminarán todas las piezas del carrusel de una sola vez.",
            confirmLabel: "Sí, eliminar carrusel",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        await api.delete(`/references/carousels/${gid}`);
        toast.success("Carrusel eliminado");
        load();
    };

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-7xl mx-auto px-10 py-14">
                    <div className="flex items-end justify-between mb-4 flex-wrap gap-4">
                        <div>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2">
                                Portafolio
                            </p>
                            <h1 className="font-display text-5xl text-fog-100">
                                Referencias
                            </h1>
                            <p className="text-fog-400 text-sm mt-2 max-w-2xl">
                                Aquí están todas las imágenes que se usan como <span className="text-periwinkle-800">base</span> para crear piezas nuevas: los logos de la marca, tu portafolio propio y la inspiración externa que guardes.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 border-b hairline border-ink-600 mb-6">
                        <TabBtn active={tab === "brand"} onClick={() => setTab("brand")} label="Marca" testId="refs-tab-brand" />
                        <TabBtn active={tab === "own"} onClick={() => setTab("own")} label="Mis Referencias" testId="refs-tab-own" />
                        <TabBtn active={tab === "external"} onClick={() => setTab("external")} label="Referencias Externas" testId="refs-tab-external" />
                    </div>

                    {tab === "brand" ? (
                        <LogosPane />
                    ) : (
                    <>
                    {/* pieces / carousels view switcher */}
                    <div className="flex items-center gap-2 mb-6">
                        <ViewPill
                            active={view === "pieces"}
                            onClick={() => setView("pieces")}
                            label="Piezas"
                            count={items.length}
                            testId="refs-view-pieces"
                        />
                        <ViewPill
                            active={view === "carousels"}
                            onClick={() => setView("carousels")}
                            label="Carruseles"
                            icon={GalleryHorizontalEnd}
                            count={carousels.length}
                            testId="refs-view-carousels"
                        />
                    </div>

                    {/* type filter chips */}
                    {view === "pieces" && (
                        <div className="flex items-center gap-2 mb-6 flex-wrap">
                            <span className="font-mono text-[10px] tracking-widest text-fog-500 uppercase mr-1">
                                Filtrar:
                            </span>
                            <TypeChip
                                active={typeFilter === "all"}
                                onClick={() => setTypeFilter("all")}
                                label="Todas"
                                color="#a5a6ff"
                                testId="refs-type-filter-all"
                            />
                            {PIECE_TYPES.map((p) => (
                                <TypeChip
                                    key={p.id}
                                    active={typeFilter === p.id}
                                    onClick={() => setTypeFilter(p.id)}
                                    label={p.label}
                                    color={p.color}
                                    testId={`refs-type-filter-${p.id}`}
                                />
                            ))}
                        </div>
                    )}

                    <div className="mb-8 rounded-2xl border hairline border-dashed border-ink-600 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                        <div className="flex-1">
                            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                Subir a {tab === "own" ? "mis referencias" : "externas"}
                            </p>
                            <p className="text-fog-100 text-sm mt-1">
                                {view === "carousels"
                                    ? "Carga un lote de imágenes; marca portada y cierre."
                                    : "Arrastra o selecciona varios archivos con su categoría."}
                            </p>
                        </div>
                        <button
                            data-testid="refs-upload-btn"
                            onClick={() => setUploadOpen(true)}
                            className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-on px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                        >
                            <Upload className="w-4 h-4" /> Subir
                        </button>
                    </div>

                    {loading && <p className="text-fog-400 font-mono text-xs">Cargando…</p>}

                    {view === "pieces" && (
                        <>
                            {!loading && items.length === 0 && (
                                <div className="rounded-2xl border hairline border-dashed border-ink-600 p-16 text-center">
                                    <p className="text-fog-400">Todavía no hay referencias aquí.</p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                                {items
                                    .filter((it) => typeFilter === "all" || it.piece_type === typeFilter)
                                    .map((it) => (
                                        <ReferenceCard
                                            key={it.id}
                                            it={it}
                                            isOpen={openAnalysisId === it.id}
                                            onToggleAnalysis={() =>
                                                setOpenAnalysisId(openAnalysisId === it.id ? null : it.id)
                                            }
                                            onReanalyze={() => reanalyze(it.id)}
                                            onDelete={() => del(it.id)}
                                            onSlugChanged={applyLocalSlug}
                                            analyzing={!!analyzing[it.id]}
                                            editableSlug={tab === "own"}
                                        />
                                    ))}
                            </div>
                            {/* Load-more controls. Shown only when there
                              * are additional pages on the server. Total
                              * count is helpful so the user knows how
                              * many references the brand has. */}
                            {!loading && items.length > 0 && (
                                <div
                                    className="mt-6 flex flex-col items-center gap-2"
                                    data-testid="refs-pagination"
                                >
                                    <p className="font-mono text-[10px] tracking-widest uppercase text-fog-500">
                                        {items.length} de {total} referencias
                                    </p>
                                    {hasMore && (
                                        <button
                                            type="button"
                                            data-testid="refs-load-more"
                                            onClick={loadMore}
                                            disabled={loadingMore}
                                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline border-periwinkle-500/50 text-periwinkle-800 hover:bg-periwinkle-500/10 text-xs font-mono uppercase tracking-widest disabled:opacity-40"
                                        >
                                            {loadingMore ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            ) : (
                                                <Sparkles className="w-3.5 h-3.5" />
                                            )}
                                            {loadingMore ? "Cargando…" : "Cargar más"}
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {view === "carousels" && (
                        <>
                            {!loading && carousels.length === 0 && (
                                <div
                                    data-testid="refs-carousels-empty"
                                    className="rounded-2xl border hairline border-dashed border-ink-600 p-16 text-center"
                                >
                                    <GalleryHorizontalEnd className="w-8 h-8 text-fog-500 mx-auto mb-3" />
                                    <p className="text-fog-300 font-medium">
                                        Aún no hay carruseles.
                                    </p>
                                    <p className="text-fog-500 text-xs mt-1">
                                        Sube un lote marcando portada y cierre para que el chat sepa cómo estructurar la pieza.
                                    </p>
                                </div>
                            )}
                            <div
                                data-testid="refs-carousels-grid"
                                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
                            >
                                {carousels.map((c) => (
                                    <CarouselFolderCard
                                        key={c.id}
                                        carousel={c}
                                        onOpen={() => setOpenCarousel(c)}
                                        onDelete={() => delCarousel(c.id)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    </>
                    )}
                </div>
            </main>
            {uploadOpen && (
                <UploadModal
                    category={tab}
                    onClose={() => setUploadOpen(false)}
                    onUploaded={load}
                />
            )}
            {openCarousel && (
                <CarouselModal
                    carousel={openCarousel}
                    onClose={() => setOpenCarousel(null)}
                    onChanged={(updatedCarousel) => {
                        // Slide added/removed inside the modal → refresh
                        // parent grid so counts/thumbs stay in sync, AND
                        // re-feed the modal so it shows the new state.
                        load();
                        if (updatedCarousel) setOpenCarousel(updatedCarousel);
                    }}
                />
            )}
        </div>
    );
}

function ReferenceCard({
    it,
    isOpen,
    onToggleAnalysis,
    onReanalyze,
    onDelete,
    onSlugChanged,
    analyzing,
    editableSlug,
}) {
    const typeMeta = PIECE_TYPES.find((p) => p.id === it.piece_type) || PIECE_TYPES[3];
    return (
        <figure
            data-testid={`ref-item-${it.id}`}
            className="group rounded-xl overflow-hidden border hairline border-ink-600 bg-ink-900 flex flex-col"
        >
            <div className="relative aspect-square overflow-hidden">
                <RetryImage
                    src={thumbUrl(it.storage_path, 320)}
                    alt={it.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
                {/* Role badge (bottom-left) — every asset here serves as a
                 * "Referencia" (base / starting point) during generation. */}
                <span
                    data-testid={`ref-role-badge-${it.id}`}
                    className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-full bg-periwinkle-500/90 text-periwinkle-ink px-2 py-0.5 font-mono text-[8px] tracking-widest uppercase shadow-sm"
                    title="Sirve como punto base al generar"
                >
                    <span className="w-1 h-1 rounded-full bg-white" />
                    Referencia
                </span>
                {/* Prominent type badge on top-left */}
                <div
                    data-testid={`ref-type-badge-${it.id}`}
                    className="absolute top-1.5 left-1.5 rounded-full glass border hairline px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase flex items-center gap-1"
                    style={{
                        borderColor: `${typeMeta.color}66`,
                        color: typeMeta.color,
                        background: "rgba(255, 255, 255, 0.92)",
                    }}
                >
                    <span
                        className="w-1 h-1 rounded-full"
                        style={{ background: typeMeta.color }}
                    />
                    {typeMeta.short}
                </div>
                <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        data-testid={`ref-analyze-${it.id}`}
                        onClick={onReanalyze}
                        disabled={analyzing}
                        className="w-7 h-7 rounded-full glass border hairline border-ink-600 text-fog-100 hover:text-periwinkle-800 flex items-center justify-center disabled:opacity-50"
                        title="Re-analizar"
                    >
                        {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    </button>
                    <button
                        data-testid={`ref-del-${it.id}`}
                        onClick={onDelete}
                        className="w-7 h-7 rounded-full glass border hairline border-ink-600 text-fog-100 hover:text-destructive flex items-center justify-center"
                    >
                        <Trash2 className="w-3 h-3" />
                    </button>
                </div>
            </div>
            <div className="p-2 space-y-1.5">
                <div className="flex items-start justify-between gap-1.5">
                    <div className="min-w-0 flex-1">
                        <p className="text-fog-100 text-xs truncate leading-tight">{it.title}</p>
                        <p
                            className="font-mono text-[8px] uppercase tracking-widest mt-0.5"
                            style={{ color: typeMeta.color }}
                        >
                            {typeMeta.label}
                        </p>
                    </div>
                    <button
                        data-testid={`ref-toggle-json-${it.id}`}
                        onClick={onToggleAnalysis}
                        className={`shrink-0 rounded-full border hairline px-1.5 py-0.5 flex items-center gap-0.5 font-mono text-[8px] tracking-wider uppercase transition-colors ${
                            it.analysis
                                ? isOpen
                                    ? "border-periwinkle-500 text-periwinkle-800 bg-ink-800"
                                    : "border-ink-600 text-fog-300 hover:border-periwinkle-500 hover:text-periwinkle-800"
                                : "border-ink-700 text-fog-500"
                        }`}
                        disabled={!it.analysis}
                        title={it.analysis ? "Ver JSON" : "Análisis pendiente…"}
                    >
                        <FileJson className="w-2.5 h-2.5" />
                        {it.analysis ? (isOpen ? "Ocultar" : "JSON") : "…"}
                    </button>
                </div>
                {isOpen && it.analysis && (
                    <div className="mt-1.5 rounded-md bg-ink-950 border hairline border-ink-600 p-2 max-h-60 overflow-y-auto">
                        <pre
                            data-testid={`ref-json-${it.id}`}
                            className="font-mono text-[9px] text-fog-300 whitespace-pre-wrap leading-relaxed"
                        >
                            {JSON.stringify(it.analysis, null, 2)}
                        </pre>
                    </div>
                )}
                {editableSlug && (
                    <SlugEditor
                        refId={it.id}
                        currentSlug={it.slug || null}
                        onSaved={(newSlug) => onSlugChanged?.(it.id, newSlug)}
                    />
                )}
            </div>
        </figure>
    );
}

/**
 * Inline editor for the @mention slug of an own-category reference.
 *
 * States:
 *   - No slug set: shows a subtle "@ Añadir mención" chip.
 *   - Slug set: shows "@slug" chip; pencil icon opens the input.
 *   - Editing: input with save/cancel; live validation + collision handled
 *     by the backend's 400/409 responses.
 */
function SlugEditor({ refId, currentSlug, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(currentSlug || "");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing) {
            setValue(currentSlug || "");
            setTimeout(() => inputRef.current?.focus(), 20);
        }
    }, [editing, currentSlug]);

    const save = async (nextRaw) => {
        const next = (nextRaw ?? "").trim().toLowerCase().replace(/^@+/, "");
        // Same regex as the backend so we don't round-trip obvious errors.
        if (next && !/^[a-z0-9][a-z0-9_-]{1,39}$/.test(next)) {
            toast.error(
                "Usa 2-40 caracteres: letras minúsculas, números, _ o -.",
            );
            return;
        }
        setSaving(true);
        try {
            const r = await api.patch(`/references/${refId}/slug`, {
                slug: next || null,
            });
            onSaved?.(r.data?.slug || null);
            setEditing(false);
            toast.success(
                next ? `Guardado como @${next}` : "Mención eliminada",
            );
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : detail?.message || "No se pudo guardar";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        await save("");
    };

    if (!editing) {
        return (
            <div className="flex items-center gap-1.5 pt-1">
                {currentSlug ? (
                    <button
                        type="button"
                        data-testid={`ref-slug-${refId}`}
                        onClick={() => setEditing(true)}
                        title="Editar mención"
                        className="min-w-0 flex-1 inline-flex items-center gap-1 rounded-full border hairline border-periwinkle-500/50 bg-periwinkle-500/10 text-periwinkle-300 hover:bg-periwinkle-500/20 px-2 py-0.5 font-mono text-[10px] tracking-wider"
                    >
                        <AtSign className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">{currentSlug}</span>
                        <Pencil className="w-2.5 h-2.5 shrink-0 opacity-60" />
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid={`ref-slug-add-${refId}`}
                        onClick={() => setEditing(true)}
                        title="Asignar un nombre para @mencionar esta referencia en el chat"
                        className="min-w-0 flex-1 inline-flex items-center gap-1 rounded-full border hairline border-dashed border-ink-600 text-fog-500 hover:text-periwinkle-400 hover:border-periwinkle-500/50 px-2 py-0.5 font-mono text-[10px] tracking-wider"
                    >
                        <AtSign className="w-2.5 h-2.5 shrink-0" />
                        <span className="truncate">Añadir mención</span>
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1 pt-1">
            <span className="text-fog-500 font-mono text-[10px] pl-0.5">@</span>
            <input
                ref={inputRef}
                data-testid={`ref-slug-input-${refId}`}
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/^@+/, ""))}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        save(value);
                    }
                    if (e.key === "Escape") {
                        e.preventDefault();
                        setEditing(false);
                    }
                }}
                placeholder="nombre_unico"
                maxLength={40}
                disabled={saving}
                className="flex-1 min-w-0 bg-ink-950 border hairline border-ink-600 rounded-md px-1.5 py-0.5 font-mono text-[10px] text-fog-100 outline-none focus:border-periwinkle-500 disabled:opacity-50"
            />
            <button
                type="button"
                data-testid={`ref-slug-save-${refId}`}
                onClick={() => save(value)}
                disabled={saving}
                className="w-5 h-5 rounded-md text-periwinkle-400 hover:bg-periwinkle-500/10 flex items-center justify-center shrink-0 disabled:opacity-50"
                title="Guardar"
            >
                {saving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                    <Check className="w-3 h-3" />
                )}
            </button>
            {currentSlug && !saving && (
                <button
                    type="button"
                    data-testid={`ref-slug-clear-${refId}`}
                    onClick={clear}
                    className="w-5 h-5 rounded-md text-fog-500 hover:text-destructive flex items-center justify-center shrink-0"
                    title="Quitar mención"
                >
                    <X className="w-3 h-3" />
                </button>
            )}
            <button
                type="button"
                data-testid={`ref-slug-cancel-${refId}`}
                onClick={() => setEditing(false)}
                disabled={saving}
                className="w-5 h-5 rounded-md text-fog-500 hover:text-fog-100 flex items-center justify-center shrink-0"
                title="Cancelar"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
}

function TypeChip({ active, onClick, label, color, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`rounded-full border hairline px-3 py-1.5 text-xs transition-all flex items-center gap-1.5 ${
                active
                    ? "bg-ink-800 font-medium"
                    : "bg-transparent text-fog-400 hover:text-fog-100"
            }`}
            style={{
                borderColor: active ? color : undefined,
                color: active ? color : undefined,
            }}
        >
            <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: color }}
            />
            {label}
        </button>
    );
}

function TabBtn({ active, onClick, label, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                active ? "text-periwinkle-800" : "text-fog-400 hover:text-fog-100"
            }`}
        >
            {label}
            {active && <span className="absolute -bottom-px left-0 right-0 h-px bg-periwinkle-500" />}
        </button>
    );
}

function ViewPill({ active, onClick, label, count, icon: Icon, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`rounded-full border hairline px-4 py-1.5 text-xs font-medium flex items-center gap-2 transition-colors ${
                active
                    ? "border-periwinkle-500 text-periwinkle-800 bg-periwinkle-500/10"
                    : "border-ink-600 text-fog-400 hover:text-fog-100"
            }`}
        >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
            <span
                className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${
                    active
                        ? "bg-periwinkle-500/20 text-periwinkle-800"
                        : "bg-ink-700 text-fog-500"
                }`}
            >
                {count}
            </span>
        </button>
    );
}

const ROLE_META = {
    cover: { label: "PORTADA", color: "#01e9bd", icon: Star },
    body: { label: "BODY", color: "#8fe7d0", icon: GalleryHorizontalEnd },
    closing: { label: "CIERRE", color: "#ffbe7b", icon: Flag },
};

function CarouselFolderCard({ carousel, onOpen, onDelete }) {
    const slides = carousel.slides || [];
    const cover =
        slides.find((s) => s.carousel_role === "cover") || slides[0];
    const pendingCount = slides.filter((s) => !s.analysis).length;

    return (
        <button
            data-testid={`refs-carousel-${carousel.id}`}
            onClick={onOpen}
            className="group relative flex flex-col rounded-2xl border hairline border-ink-600 bg-ink-900 overflow-hidden text-left hover:border-periwinkle-500 transition-all hover:-translate-y-0.5"
        >
            {/* Stacked-folder look: two slanted paper edges behind the cover */}
            <div className="relative px-3 pt-3">
                <span
                    aria-hidden
                    className="absolute -top-1 left-4 right-4 h-3 rounded-t-xl bg-ink-800 border hairline border-ink-600"
                    style={{ transform: "rotate(-1.2deg)" }}
                />
                <span
                    aria-hidden
                    className="absolute -top-0.5 left-5 right-5 h-3 rounded-t-xl bg-ink-900 border hairline border-ink-600"
                    style={{ transform: "rotate(0.6deg)" }}
                />
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden border hairline border-ink-600 bg-ink-950">
                    {cover ? (
                        <RetryImage
                            src={thumbUrl(cover.storage_path, 480)}
                            alt={carousel.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-fog-500">
                            <Folder className="w-10 h-10" />
                        </div>
                    )}
                    {/* count badge top-right */}
                    <span
                        className="absolute top-2 right-2 rounded-full bg-periwinkle-500 text-periwinkle-ink px-2 py-0.5 font-mono text-[10px] tracking-widest uppercase shadow-glow-sm inline-flex items-center gap-1"
                        data-testid={`refs-carousel-count-${carousel.id}`}
                    >
                        <GalleryHorizontalEnd className="w-3 h-3" />
                        {slides.length}
                    </span>
                    {pendingCount > 0 && (
                        <span className="absolute top-2 left-2 rounded-full bg-amber-400 text-ink-on px-1.5 py-0.5 font-mono text-[9px] tracking-widest uppercase inline-flex items-center gap-1">
                            <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            {pendingCount}
                        </span>
                    )}
                </div>
            </div>

            <div className="px-4 pt-4 pb-3 flex items-center gap-2 min-w-0">
                <Folder
                    className="w-4 h-4 text-periwinkle-800 shrink-0"
                    fill="currentColor"
                />
                <span className="flex-1 min-w-0">
                    <span className="block font-display text-sm text-fog-100 truncate">
                        {carousel.title || "Carrusel"}
                    </span>
                    <span className="block font-mono text-[9px] tracking-widest text-fog-400 uppercase mt-0.5">
                        {slides.length} slide{slides.length === 1 ? "" : "s"} · abrir
                    </span>
                </span>
                <span
                    role="button"
                    tabIndex={0}
                    data-testid={`refs-carousel-delete-${carousel.id}`}
                    onClick={(ev) => {
                        ev.stopPropagation();
                        onDelete();
                    }}
                    onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                            ev.stopPropagation();
                            onDelete();
                        }
                    }}
                    aria-label="Eliminar carrusel"
                    className="shrink-0 w-7 h-7 rounded-md text-fog-500 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </span>
            </div>
        </button>
    );
}

function CarouselModal({ carousel, onClose, onChanged }) {
    const [showJson, setShowJson] = useState(null); // slide id currently expanded
    const [deletingId, setDeletingId] = useState(null);
    const [uploadingRole, setUploadingRole] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    // Drag-and-drop reorder state. We only allow reordering inside the BODY
    // section (cover/closing are unique by rule). `dragId` is the id of the
    // slide currently being dragged; `dragOverId` is the id of the slide we
    // are hovering over so we can highlight the drop target. `reordering`
    // is a transient flag during the PATCH call to gate further drags.
    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [reordering, setReordering] = useState(false);
    const fileInputRef = useRef(null);
    const pendingRoleRef = useRef(null);
    const confirm = useConfirm();

    const slides = [...(carousel.slides || [])].sort(
        (a, b) => (a.carousel_position || 0) - (b.carousel_position || 0),
    );
    const groupId = carousel.id;
    const category = carousel.category || "own";

    // Group slides by role so the modal reads: PORTADA · CUERPO · CIERRE.
    const groups = [
        { key: "cover", label: "Portada", items: slides.filter((s) => s.carousel_role === "cover") },
        { key: "body", label: "Cuerpo", items: slides.filter((s) => s.carousel_role === "body") },
        { key: "closing", label: "Cierre", items: slides.filter((s) => s.carousel_role === "closing") },
    ];
    const visibleGroups = groups.filter((g) => g.items.length > 0 || g.key === uploadingRole);

    // Fetch the latest version of THIS carousel from the server. We re-read
    // the whole list and pick the matching id — the carousels endpoint is
    // cheap and already used by the parent, so this keeps things simple
    // without introducing a per-carousel GET endpoint.
    const fetchUpdated = async () => {
        try {
            const r = await api.get(`/references/carousels?category=${category}`);
            const next = (r.data || []).find((c) => c.id === groupId);
            return next || null;
        } catch {
            return null;
        }
    };

    const handleDeleteSlide = async (slide) => {
        // Guard against orphaning the carousel: if this is the LAST slide,
        // tell the user to use the "Eliminar carrusel" action instead.
        if (slides.length <= 1) {
            toast.error(
                "Es la última imagen. Para eliminar el carrusel completo, usa el botón rojo de la card.",
            );
            return;
        }
        // If the slide is the only cover and there's no body/closing left to
        // be promoted, ask the user to confirm — the carousel will keep
        // working but loses its cover.
        const willLoseCover =
            slide.carousel_role === "cover" &&
            groups.find((g) => g.key === "cover")?.items.length === 1;
        const desc = willLoseCover
            ? "Esta es la única portada del carrusel. Después podrás subir una nueva como portada."
            : "Esta imagen se eliminará del carrusel. Esta acción no se puede deshacer.";
        const ok = await confirm({
            title: `¿Eliminar slide #${slide.carousel_position}?`,
            description: desc,
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        setDeletingId(slide.id);
        try {
            await api.delete(`/references/${slide.id}`);
            toast.success(`Slide eliminado`);
            const updated = await fetchUpdated();
            onChanged?.(updated);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo eliminar el slide",
            );
        } finally {
            setDeletingId(null);
        }
    };

    const triggerAdd = (role) => {
        if (uploadingRole) return;
        // Cover and closing accept ONE piece each — block before opening the
        // picker so the user gets a useful message instead of a 409 later.
        if (role !== "body") {
            const has = groups.find((g) => g.key === role)?.items.length > 0;
            if (has) {
                const label = role === "cover" ? "una portada" : "un cierre";
                toast.error(
                    `Este carrusel ya tiene ${label}. Elimina la actual antes de subir otra.`,
                );
                return;
            }
        }
        pendingRoleRef.current = role;
        // Reset value so picking the same files twice still fires `change`.
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Single-file for cover/closing, multi for body — matches the
        // structural rule "1 cover + 1 closing + N body".
        if (fileInputRef.current) {
            fileInputRef.current.multiple = role === "body";
        }
        fileInputRef.current?.click();
    };

    // Drop handler — runs when the user releases a body slide over another
    // body slide. We move the dragged slide to the position right BEFORE
    // the target, then re-index the whole body section sequentially.
    // Positions are global (cover=1, body=2..N, closing=N+1) so we preserve
    // the slot boundaries by only touching body positions.
    const handleDropOn = async (targetId) => {
        const fromId = dragId;
        setDragId(null);
        setDragOverId(null);
        if (!fromId || !targetId || fromId === targetId) return;
        const body = groups.find((g) => g.key === "body")?.items || [];
        const ids = body.map((s) => s.id);
        const fromIdx = ids.indexOf(fromId);
        const toIdx = ids.indexOf(targetId);
        if (fromIdx < 0 || toIdx < 0) return;
        const next = [...body];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        // Re-index using the existing body positions. We keep the SET of
        // positions stable (so we don't collide with cover/closing) and
        // just rotate them across the new order.
        const usedPositions = body
            .map((s) => s.carousel_position)
            .sort((a, b) => a - b);
        const positions = next.map((s, i) => ({
            id: s.id,
            position: usedPositions[i],
        }));
        setReordering(true);
        try {
            await api.patch(
                `/references/carousels/${groupId}/reorder`,
                { positions },
            );
            const updated = await fetchUpdated();
            onChanged?.(updated);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo reordenar",
            );
        } finally {
            setReordering(false);
        }
    };

    // Change a slide's role (cover/body/closing). When the target slot is
    // a unique one (cover/closing) that's already filled, the backend
    // SWAPS the two slides — we just refetch and the UI catches up. The
    // user sees both roles change at once.
    const [changingRoleId, setChangingRoleId] = useState(null);
    const handleChangeRole = async (slide, newRole) => {
        if (changingRoleId || reordering) return;
        if (slide.carousel_role === newRole) return;
        setChangingRoleId(slide.id);
        try {
            const r = await api.patch(`/references/${slide.id}/role`, {
                role: newRole,
            });
            const updated = await fetchUpdated();
            onChanged?.(updated);
            const niceRole = { cover: "Portada", body: "Cuerpo", closing: "Cierre" }[
                newRole
            ];
            toast.success(
                r.data?.swapped
                    ? `Movido a ${niceRole} (intercambiado con la pieza existente)`
                    : `Movido a ${niceRole}`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo cambiar el rol",
            );
        } finally {
            setChangingRoleId(null);
        }
    };


    const handleFiles = async (files) => {
        const role = pendingRoleRef.current;
        pendingRoleRef.current = null;
        let list = Array.from(files || []).filter((f) =>
            f.type.startsWith("image/"),
        );
        // Defense in depth: even if the input element accepted multiple,
        // cover/closing can only take 1. Drop the extras silently and tell
        // the user.
        if (role && role !== "body" && list.length > 1) {
            toast.info(
                role === "cover"
                    ? "La portada acepta solo 1 imagen — usaremos la primera."
                    : "El cierre acepta solo 1 imagen — usaremos la primera.",
            );
            list = list.slice(0, 1);
        }
        if (!role || list.length === 0) return;
        // Compute the next position so new slides don't collide with the
        // existing ones. carousel_total is left advisory (the backend
        // doesn't enforce consistency across slides of the same group).
        const maxPos = slides.reduce(
            (m, s) => Math.max(m, s.carousel_position || 0),
            0,
        );
        setUploadingRole(role);
        setProgress({ current: 0, total: list.length });
        let added = 0;
        let failed = 0;
        for (let i = 0; i < list.length; i++) {
            const f = list[i];
            setProgress({ current: i + 1, total: list.length });
            const fd = new FormData();
            fd.append("file", f);
            fd.append("category", category);
            fd.append("carousel_role", role);
            fd.append("carousel_position", String(maxPos + i + 1));
            fd.append("carousel_total", String(slides.length + list.length));
            fd.append("carousel_group_id", groupId);
            // Re-send the title so all slides keep a consistent label.
            if (carousel.title) fd.append("carousel_group_title", carousel.title);
            try {
                await api.post("/references/upload/carousel-slide", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                added += 1;
            } catch (e) {
                failed += 1;
                toast.error(
                    `Error subiendo ${f.name}: ${e?.response?.data?.detail || "intenta de nuevo"}`,
                );
            }
        }
        setUploadingRole(null);
        setProgress({ current: 0, total: 0 });
        if (added > 0) {
            const updated = await fetchUpdated();
            onChanged?.(updated);
            toast.success(
                `${added} ${added === 1 ? "imagen agregada" : "imágenes agregadas"} a "${
                    { cover: "Portada", body: "Cuerpo", closing: "Cierre" }[role]
                }"${failed ? ` · ${failed} fallaron` : ""}`,
            );
        }
    };

    return (
        <div
            data-testid="refs-carousel-modal"
            className="fixed inset-0 z-[80] bg-ink-950/95 backdrop-blur-sm flex flex-col animate-fade-in"
            onClick={onClose}
        >
            <div
                className="max-w-6xl w-full mx-auto my-8 bg-ink-950 border hairline border-ink-600 rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[calc(100vh-64px)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* hidden file input — shared by all three role sections,
                    the role being uploaded is tracked in pendingRoleRef. */}
                <input
                    ref={fileInputRef}
                    data-testid="refs-carousel-modal-file-input"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
                {/* header */}
                <header className="shrink-0 flex items-center gap-4 px-6 py-4 border-b hairline border-ink-600">
                    <Folder
                        className="w-5 h-5 text-periwinkle-800"
                        fill="currentColor"
                    />
                    <div className="min-w-0">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                            Carrusel · {slides.length} imágenes
                        </p>
                        <h2 className="font-display text-2xl text-fog-100 truncate">
                            {carousel.title || "Carrusel"}
                        </h2>
                    </div>
                    <div className="flex-1" />
                    <button
                        data-testid="refs-carousel-modal-close"
                        onClick={onClose}
                        className="w-10 h-10 rounded-full border hairline border-ink-600 text-fog-300 hover:text-fog-100 hover:border-periwinkle-500 flex items-center justify-center transition-colors"
                        aria-label="Cerrar"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                {/* body — roles sections */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {visibleGroups.map((g) => {
                        const rm = ROLE_META[g.key];
                        const isUploadingHere = uploadingRole === g.key;
                        return (
                            <section
                                key={g.key}
                                data-testid={`refs-carousel-section-${g.key}`}
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <span
                                        className="inline-block w-2 h-2 rounded-full"
                                        style={{ background: rm.color }}
                                    />
                                    <h3 className="font-display text-lg text-fog-100">
                                        {g.label}
                                    </h3>
                                    <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                        {g.items.length} pieza{g.items.length === 1 ? "" : "s"}
                                    </span>
                                    {g.key === "body" && g.items.length > 1 && (
                                        <span
                                            data-testid="refs-carousel-reorder-hint"
                                            className="font-mono text-[9px] tracking-widest text-fog-500 uppercase hidden sm:inline-flex items-center gap-1"
                                            title="Arrastra para reordenar"
                                        >
                                            <GripVertical className="w-3 h-3" />
                                            Arrastra para reordenar
                                        </span>
                                    )}
                                    <div className="flex-1" />
                                    {g.key !== "body" && g.items.length >= 1 ? (
                                        <span
                                            data-testid={`refs-carousel-full-${g.key}`}
                                            title={
                                                g.key === "cover"
                                                    ? "El carrusel ya tiene portada (máx 1)"
                                                    : "El carrusel ya tiene cierre (máx 1)"
                                            }
                                            className="rounded-md border hairline border-ink-700 bg-ink-900/60 text-fog-500 px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase flex items-center gap-1.5"
                                        >
                                            <CheckCircle2 className="w-3 h-3" />
                                            Completo
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid={`refs-carousel-add-${g.key}`}
                                            onClick={() => triggerAdd(g.key)}
                                            disabled={!!uploadingRole}
                                            className="rounded-md border hairline border-ink-600 text-fog-300 hover:text-periwinkle-800 hover:border-periwinkle-500 px-2.5 py-1 font-mono text-[10px] tracking-widest uppercase flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            title={`Subir más imágenes a ${g.label.toLowerCase()}`}
                                        >
                                            {isUploadingHere ? (
                                                <>
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                    {progress.current}/{progress.total}
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-3 h-3" />
                                                    Agregar
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {g.items.map((s) => {
                                        const isDeleting = deletingId === s.id;
                                        // Body slides are reorderable; cover/closing aren't
                                        // (there's only one of each, dragging it is a no-op).
                                        const draggable = g.key === "body" && g.items.length > 1;
                                        const isDragging = dragId === s.id;
                                        const isDropTarget = dragOverId === s.id && dragId !== s.id;
                                        return (
                                            <figure
                                                key={s.id}
                                                data-testid={`refs-carousel-slide-${s.id}`}
                                                draggable={draggable && !reordering}
                                                onDragStart={
                                                    draggable
                                                        ? (e) => {
                                                              setDragId(s.id);
                                                              e.dataTransfer.effectAllowed = "move";
                                                              // Required for Firefox.
                                                              try {
                                                                  e.dataTransfer.setData("text/plain", s.id);
                                                              } catch {}
                                                          }
                                                        : undefined
                                                }
                                                onDragOver={
                                                    draggable
                                                        ? (e) => {
                                                              if (!dragId || dragId === s.id) return;
                                                              e.preventDefault();
                                                              e.dataTransfer.dropEffect = "move";
                                                              if (dragOverId !== s.id) setDragOverId(s.id);
                                                          }
                                                        : undefined
                                                }
                                                onDragLeave={
                                                    draggable
                                                        ? () => {
                                                              if (dragOverId === s.id) setDragOverId(null);
                                                          }
                                                        : undefined
                                                }
                                                onDrop={
                                                    draggable
                                                        ? (e) => {
                                                              e.preventDefault();
                                                              handleDropOn(s.id);
                                                          }
                                                        : undefined
                                                }
                                                onDragEnd={
                                                    draggable
                                                        ? () => {
                                                              setDragId(null);
                                                              setDragOverId(null);
                                                          }
                                                        : undefined
                                                }
                                                className={`relative group rounded-xl overflow-hidden border hairline bg-ink-900 transition-all ${
                                                    isDeleting ? "opacity-40" : ""
                                                } ${
                                                    isDragging
                                                        ? "border-periwinkle-500 ring-2 ring-periwinkle-500/30 opacity-60 scale-[0.98]"
                                                        : isDropTarget
                                                          ? "border-periwinkle-500 ring-4 ring-periwinkle-500/40"
                                                          : "border-ink-600"
                                                } ${draggable && !reordering ? "cursor-grab active:cursor-grabbing" : ""}`}
                                            >
                                                <div className="relative aspect-square">
                                                    <RetryImage
                                                        src={thumbUrl(s.storage_path, 480)}
                                                        alt={s.title}
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <span
                                                        className="absolute top-2 left-2 rounded-full px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase bg-white/90 text-ink-on border hairline"
                                                        style={{
                                                            borderColor: `${rm.color}99`,
                                                            color: rm.color === "#01e9bd" ? "#00836c" : rm.color,
                                                        }}
                                                    >
                                                        {rm.label} · #{s.carousel_position}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        data-testid={`refs-carousel-slide-delete-${s.id}`}
                                                        disabled={isDeleting || !!uploadingRole}
                                                        onClick={() => handleDeleteSlide(s)}
                                                        title="Eliminar este slide"
                                                        aria-label={`Eliminar slide ${s.carousel_position}`}
                                                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-ink-950/85 backdrop-blur border hairline border-ink-600 text-fog-400 hover:text-destructive hover:border-destructive opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                                                    >
                                                        {isDeleting ? (
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-3 h-3" />
                                                        )}
                                                    </button>
                                                    <SlideRoleMenu
                                                        slide={s}
                                                        disabled={
                                                            isDeleting ||
                                                            !!uploadingRole ||
                                                            changingRoleId === s.id ||
                                                            !!reordering
                                                        }
                                                        busy={changingRoleId === s.id}
                                                        onChange={(role) => handleChangeRole(s, role)}
                                                    />
                                                </div>
                                                <figcaption className="px-3 py-2 flex items-center justify-between gap-2">
                                                    <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase truncate">
                                                        Slide {s.carousel_position}
                                                    </span>
                                                    {s.analysis ? (
                                                        <button
                                                            data-testid={`refs-carousel-slide-json-${s.id}`}
                                                            onClick={() =>
                                                                setShowJson(
                                                                    showJson === s.id ? null : s.id,
                                                                )
                                                            }
                                                            className="shrink-0 rounded-full border hairline border-ink-600 px-2 py-0.5 flex items-center gap-1 font-mono text-[9px] tracking-wider uppercase text-fog-300 hover:border-periwinkle-500 hover:text-periwinkle-800"
                                                        >
                                                            <FileJson className="w-3 h-3" />
                                                            {showJson === s.id ? "ocultar" : "json"}
                                                        </button>
                                                    ) : (
                                                        <Loader2 className="w-3 h-3 text-fog-500 animate-spin" />
                                                    )}
                                                </figcaption>
                                                {showJson === s.id && s.analysis && (
                                                    <div className="border-t hairline border-ink-600 bg-ink-950 p-2 max-h-40 overflow-y-auto">
                                                        <pre className="font-mono text-[9px] text-fog-300 whitespace-pre-wrap leading-relaxed">
                                                            {JSON.stringify(s.analysis, null, 2)}
                                                        </pre>
                                                    </div>
                                                )}
                                            </figure>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}
                    {/* Add buttons for empty role sections (cover/body/closing
                        with 0 items) so the user can ALWAYS add a new
                        section — e.g., upload a cover after deleting it. */}
                    {groups
                        .filter((g) => g.items.length === 0 && uploadingRole !== g.key)
                        .map((g) => (
                            <section
                                key={`empty-${g.key}`}
                                data-testid={`refs-carousel-section-empty-${g.key}`}
                                className="rounded-xl border hairline border-dashed border-ink-600 px-5 py-4 flex items-center gap-3"
                            >
                                <span
                                    className="inline-block w-2 h-2 rounded-full"
                                    style={{ background: ROLE_META[g.key].color }}
                                />
                                <h3 className="font-display text-base text-fog-300">
                                    {g.label}
                                </h3>
                                <span className="font-mono text-[10px] tracking-widest text-fog-500 uppercase">
                                    Sin piezas
                                </span>
                                <div className="flex-1" />
                                <button
                                    type="button"
                                    data-testid={`refs-carousel-add-empty-${g.key}`}
                                    onClick={() => triggerAdd(g.key)}
                                    disabled={!!uploadingRole}
                                    className="rounded-md border hairline border-periwinkle-500/50 text-periwinkle-800 hover:border-periwinkle-500 px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <Upload className="w-3 h-3" />
                                    Agregar {g.label.toLowerCase()}
                                </button>
                            </section>
                        ))}
                </div>
            </div>
        </div>
    );
}


/**
 * Brand logos pane — surfaced under /references as the first tab.
 * Uses the existing `/api/brand/logos` endpoint (same collection the chat's
 * auto-brand-reference feature consumes). Lets the user upload every logo
 * variant (full, icon, wordmark, white, dark…) using a free-text label.
 */
function LogosPane() {
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [label, setLabel] = useState("");
    const [uploading, setUploading] = useState(false);
    const confirm = useConfirm();
    const fileRef = useRef(null);

    const reload = () => {
        setLoading(true);
        api.get("/brand/logos")
            .then((r) => setLogos(r.data || []))
            .finally(() => setLoading(false));
    };
    useEffect(reload, []);
    useEffect(() => {
        const onBrand = () => reload();
        window.addEventListener("kc:brand-changed", onBrand);
        return () => window.removeEventListener("kc:brand-changed", onBrand);
    }, []);

    const handleFiles = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("label", label || f.name);
                await api.post("/brand/logos", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            toast.success(`${files.length} logo${files.length === 1 ? "" : "s"} subido${files.length === 1 ? "" : "s"}`);
            setLabel("");
            if (fileRef.current) fileRef.current.value = "";
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo subir el logo");
        } finally {
            setUploading(false);
        }
    };

    const del = async (lg) => {
        const ok = await confirm({
            kind: "delete",
            title: "¿Eliminar este logo?",
            body: `"${lg.label || "Sin nombre"}" se borrará permanentemente.`,
            confirmLabel: "Eliminar",
            tone: "destructive",
        });
        if (!ok) return;
        try {
            await api.delete(`/brand/logos/${lg.id}`);
            toast.success("Logo eliminado");
            reload();
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    return (
        <section data-testid="refs-brand-pane" className="space-y-6">
            <div
                className="rounded-2xl border hairline border-dashed border-periwinkle-500/50 bg-periwinkle-500/5 p-6"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    handleFiles(Array.from(e.dataTransfer.files || []));
                }}
            >
                <p className="font-mono text-[10px] tracking-widest text-periwinkle-800 uppercase">
                    Logos de la marca
                </p>
                <p className="text-fog-100 text-sm mt-1">
                    Sube todas las versiones: full lockup, iconotipo, wordmark, blanco, sobre
                    oscuro, etc. Se usan automáticamente como referencia cuando generes piezas
                    sin adjuntar imágenes.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input
                        type="text"
                        data-testid="brand-logo-label"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Variante (ej: Iconotipo · fondo blanco)"
                        className="flex-1 rounded-xl border hairline border-ink-600 bg-ink-900 px-4 py-2.5 text-sm text-fog-100 placeholder-fog-500 focus:outline-none focus:border-periwinkle-500"
                    />
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        multiple
                        hidden
                        data-testid="brand-logo-file-input"
                        onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                    />
                    <button
                        data-testid="brand-logo-upload-btn"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-periwinkle-ink px-5 py-2.5 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                    >
                        {uploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Upload className="w-4 h-4" />
                        )}
                        {uploading ? "Subiendo…" : "Subir logo"}
                    </button>
                </div>
                <p className="text-[10px] font-mono text-fog-400 mt-2">
                    O arrastra varios archivos aquí. Formatos: PNG · JPG · WEBP · SVG.
                </p>
            </div>

            {loading && <p className="text-fog-400 font-mono text-xs">Cargando…</p>}

            {!loading && logos.length === 0 && (
                <div className="rounded-2xl border hairline border-dashed border-ink-600 p-16 text-center">
                    <p className="text-fog-400">
                        Todavía no hay logos cargados para esta marca.
                    </p>
                </div>
            )}

            {!loading && logos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {logos.map((lg) => (
                        <figure
                            key={lg.id}
                            data-testid={`brand-logo-card-${lg.id}`}
                            className="group rounded-xl overflow-hidden border hairline border-ink-600 bg-ink-900 flex flex-col"
                        >
                            <div className="relative aspect-square overflow-hidden bg-white">
                                <RetryImage
                                    src={thumbUrl(lg.storage_path, 320)}
                                    alt={lg.label}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-contain p-3"
                                />
                                <button
                                    data-testid={`brand-logo-delete-${lg.id}`}
                                    onClick={() => del(lg)}
                                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                                    aria-label="Eliminar logo"
                                    title="Eliminar"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <figcaption className="px-3 py-2 text-[11px] text-fog-100 truncate">
                                {lg.label || "Sin nombre"}
                            </figcaption>
                        </figure>
                    ))}
                </div>
            )}
        </section>
    );
}


/**
 * Compact role-change menu for a carousel slide.
 *
 * Renders a small swap pill in the top-LEFT (mirror of the delete button
 * on the right). Clicking it toggles a dropdown with the other two roles
 * — the current role is excluded. When the target role is cover/closing
 * and that slot is already occupied, the backend swaps the two slides;
 * we surface that fact in the toast string.
 *
 * Closes itself when the user clicks anywhere outside the dropdown.
 */
function SlideRoleMenu({ slide, disabled, busy, onChange }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (!rootRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const choices = [
        { key: "cover", label: "Portada" },
        { key: "body", label: "Cuerpo" },
        { key: "closing", label: "Cierre" },
    ].filter((c) => c.key !== slide.carousel_role);
    return (
        <div
            ref={rootRef}
            className="absolute top-2 left-2 z-10"
            data-testid={`refs-carousel-slide-role-menu-${slide.id}`}
        >
            <button
                type="button"
                disabled={disabled}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) setOpen((o) => !o);
                }}
                title="Mover a otra sección"
                aria-label="Cambiar rol del slide"
                data-testid={`refs-carousel-slide-role-trigger-${slide.id}`}
                className="w-7 h-7 rounded-full bg-ink-950/85 backdrop-blur border hairline border-ink-600 text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {busy ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                    <ArrowRightLeft className="w-3 h-3" />
                )}
            </button>
            {open && (
                <div className="absolute top-9 left-0 min-w-[140px] rounded-xl border hairline border-ink-600 bg-ink-950/95 backdrop-blur p-1 shadow-2xl">
                    <p className="font-mono text-[9px] tracking-widest text-fog-500 uppercase px-2 py-1">
                        Mover a
                    </p>
                    {choices.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            data-testid={`refs-carousel-slide-role-${c.key}-${slide.id}`}
                            onClick={() => {
                                setOpen(false);
                                onChange?.(c.key);
                            }}
                            className="w-full text-left rounded-md px-2 py-1.5 text-xs text-fog-200 hover:bg-periwinkle-500/10 hover:text-periwinkle-800 flex items-center gap-2"
                        >
                            <span
                                className="inline-block w-1.5 h-1.5 rounded-full"
                                style={{ background: ROLE_META[c.key].color }}
                            />
                            {c.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
